/**
 * imageIntent – central source of truth for context-aware image quality rules.
 *
 * Instead of hardcoding resolution thresholds in each component, callsites
 * declare their *intent* (hero, product, gallery, thumbnail) and this module
 * returns the appropriate thresholds and copy.
 *
 * This keeps one shared image component usable everywhere while applying
 * context-appropriate quality guidance.
 *
 * Thresholds are based on longest-side (px).
 */

/** The visual purpose of the image being uploaded / assessed. */
export type ImageIntent =
  | 'hero'          // Place / area / producer / landing hero/cover – high bar
  | 'product'       // Product cover or activity cover – medium bar
  | 'gallery'       // Gallery images (any entity) – relaxed bar
  | 'thumbnail';    // Admin preview, list card – minimal requirements

export interface ImageIntentThresholds {
  /** Longest side must be ≥ this to pass without any warning. */
  goodWidth: number;
  /** Longest side ≥ this shows a soft warning but is accepted. */
  warnWidth: number;
  /** Longest side < this blocks the upload. */
  blockWidth: number;
  /** Recommended width mentioned in user-facing copy. */
  recommendedWidth: number;
  /** Swedish label for the context shown in hints/copy. */
  contextLabel: string;
}

const INTENT_THRESHOLDS: Record<ImageIntent, ImageIntentThresholds> = {
  hero: {
    goodWidth: 1600,
    warnWidth: 1200,
    blockWidth: 1200,
    recommendedWidth: 1600,
    contextLabel: 'omslagsbild',
  },
  product: {
    goodWidth: 1000,
    warnWidth: 600,
    blockWidth: 400,
    recommendedWidth: 1000,
    contextLabel: 'produktbild',
  },
  gallery: {
    goodWidth: 1000,
    warnWidth: 800,
    blockWidth: 600,
    recommendedWidth: 1000,
    contextLabel: 'galleribild',
  },
  thumbnail: {
    goodWidth: 400,
    warnWidth: 200,
    blockWidth: 200,
    recommendedWidth: 400,
    contextLabel: 'miniatyrbild',
  },
};

/**
 * Returns the quality thresholds for a given image intent.
 * Central lookup — all callsites go through here.
 */
export function getIntentThresholds(intent: ImageIntent): ImageIntentThresholds {
  return INTENT_THRESHOLDS[intent];
}

/**
 * Maps the legacy ImageUsage ('cover' | 'gallery') to an ImageIntent.
 *
 * @deprecated — Prefer passing an explicit ImageIntent instead of relying
 * on this mapping.  'cover' → 'hero' is only correct for place/area/producer
 * hero images.  Product / event / accommodation covers should use 'product'.
 * This function is kept for backward compatibility with callers that still
 * pass the raw kind string.
 */
export function usageToIntent(usage: 'cover' | 'gallery'): ImageIntent {
  return usage === 'cover' ? 'hero' : 'gallery';
}

// ── Variant selection policy ───────────────────────────

import type { VariantWidth } from './mediaTypes';

/**
 * Preferred display variant for a given intent.
 *
 * This is the recommended variant width to request from resolveAssetUrl
 * for normal rendering. Callers rendering at a specific known size can
 * still pass an explicit variant.
 *
 * - hero  → 1280  (largest – hero carries the experience)
 * - product → 640  (sufficient for cards/detail; 1280 used if hero-like)
 * - gallery → 640  (grid/list; lightbox callsites may override to 1280)
 * - thumbnail → 320  (list cards, admin previews)
 */
const INTENT_VARIANT: Record<ImageIntent, VariantWidth> = {
  hero: 1280,
  product: 640,
  gallery: 640,
  thumbnail: 320,
};

export function intentPreferredVariant(intent: ImageIntent): VariantWidth {
  return INTENT_VARIANT[intent];
}

/**
 * Ordered fallback preference for each variant width.
 * When the preferred variant is missing we try these in order.
 *
 * Large-first for large requests (hero should never get 320 if 640 exists),
 * small-first for small requests (thumbnail shouldn't pull 1280 unnecessarily).
 */
export const VARIANT_FALLBACK_ORDER: Record<VariantWidth, readonly VariantWidth[]> = {
  1280: [1280, 640, 320],
  640: [640, 1280, 320],
  320: [320, 640, 1280],
} as const;

/**
 * Determines the default ImageIntent based on ownerType and kind.
 * Callsites can override this by passing an explicit intent.
 */
export function defaultImageIntent(
  ownerType: string,
  kind: 'cover' | 'gallery',
): ImageIntent {
  if (kind === 'gallery') return 'gallery';

  // Cover images: hero-level for places/areas/producers, product-level for products/events
  switch (ownerType) {
    case 'place':
    case 'area':
    case 'producer':
    case 'producer_shop':
    case 'landing':
      return 'hero';
    case 'product':
    case 'event':
    case 'flea_market':
    case 'accommodation':
      return 'product';
    default:
      return 'product';
  }
}
